import React, { useState } from "react";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import ContentPasteGoIcon from "@mui/icons-material/ContentPasteGo";
import { Box, Typography } from "@mui/material";
import { Alert } from "@components/Alert/Alert";
import Button from "@components/Button/Button";
import { useTranslation } from "react-i18next";
import { DRYBULK_CARGO_OPS_LANG_BASE, PENDING_APPROVAL_STATUS } from "../constants";
import { useCachedCargoOperationDetail } from "../hooks/useCargoOperationDetail";
import { useOperationReview } from "../hooks/useOperationReview";
import ReviewDecisionDialog from "./ReviewDecisionDialog";

const L = `${DRYBULK_CARGO_OPS_LANG_BASE}.review`;

interface OperationsReviewActionsProps {
  operationId: string;
  /** The host pauses its detail polling while a decision dialog is open (an edit in progress must not be disturbed). */
  onDialogOpenChange?: (open: boolean) => void;
}

const OperationsReviewActions: React.FC<OperationsReviewActionsProps> = ({
  operationId,
  onDialogOpenChange,
}) => {
  const { t } = useTranslation();
  const detail = useCachedCargoOperationDetail(operationId).data;
  const review = useOperationReview(operationId);
  const [dialog, setDialog] = useState<"approve" | "reject" | null>(null);

  const setDialogOpen = (next: "approve" | "reject" | null) => {
    setDialog(next);
    onDialogOpenChange?.(next !== null);
  };

  if (!detail || detail.status !== PENDING_APPROVAL_STATUS) {
    return null;
  }

  const task = detail.reviewTask ?? null;
  const claimed = task !== null && task.status === "CLAIMED";
  const byCaller = claimed && task.claimedByCaller;
  const byAnother = claimed && !task.claimedByCaller;
  const busy = review.isClaiming || review.progress === "processing";

  const confirm = async (text: string) => {
    if (dialog === null) {
      return;
    }
    const accepted = await review.submitDecision(dialog, text);
    if (accepted) {
      setDialogOpen(null);
    }
  };

  return (
    <Box
      data-testid="operation-review"
      sx={{ display: "flex", flexDirection: "column", gap: 1, alignItems: "flex-end" }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
        {!claimed && (
          <Button
            variant="contained"
            startIcon={<ContentPasteGoIcon />}
            disabled={busy}
            onClick={review.claim}
            testId="operation-review-claim"
            label={<span className="normal-case font-medium">{t(`${L}.claim`)}</span>}
          />
        )}
        {byCaller && (
          <>
            <Button
              variant="outlined"
              disabled={busy}
              onClick={review.unclaim}
              testId="operation-review-unclaim"
              label={<span className="normal-case font-medium">{t(`${L}.unclaim`)}</span>}
            />
            <Button
              variant="outlined"
              startIcon={<CancelOutlinedIcon />}
              disabled={busy}
              onClick={() => setDialogOpen("reject")}
              testId="operation-review-reject"
              label={<span className="normal-case font-medium">{t(`${L}.reject`)}</span>}
            />
            <Button
              variant="contained"
              startIcon={<ContentPasteGoIcon />}
              disabled={busy}
              onClick={() => setDialogOpen("approve")}
              testId="operation-review-approve"
              label={<span className="normal-case font-medium">{t(`${L}.approve`)}</span>}
            />
          </>
        )}
        {byAnother && (
          <Typography variant="body2" data-testid="operation-review-badge">
            {t(`${L}.badge.claimed-by`, { owner: task.owner ?? "" })}
          </Typography>
        )}
      </Box>
      {review.progress === "processing" && (
        <div data-testid="operation-review-processing">
          <Alert type="info">{review.messages.processing}</Alert>
        </div>
      )}
      {review.progress === "timed-out" && (
        <div data-testid="operation-review-still-processing">
          <Alert type="warning">{review.messages.stillProcessing}</Alert>
        </div>
      )}
      <ReviewDecisionDialog
        open={dialog === "approve"}
        testId="operation-review-approve-dialog"
        title={t(`${L}.approve-dialog.title`)}
        label={t(`${L}.approve-dialog.label`)}
        confirmLabel={t(`${L}.approve-dialog.confirm`)}
        required={false}
        isSubmitting={review.isDeciding}
        onConfirm={(text) => void confirm(text)}
        onClose={() => setDialogOpen(null)}
      />
      <ReviewDecisionDialog
        open={dialog === "reject"}
        testId="operation-review-reject-dialog"
        title={t(`${L}.reject-dialog.title`)}
        label={t(`${L}.reject-dialog.label`)}
        confirmLabel={t(`${L}.reject-dialog.confirm`)}
        required
        isSubmitting={review.isDeciding}
        onConfirm={(text) => void confirm(text)}
        onClose={() => setDialogOpen(null)}
      />
    </Box>
  );
};

export default OperationsReviewActions;
